import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import getHolidayRegionListing from "services/get-holiday-region-listing";
import { DEFAULT_HOLIDAY_REGION, getHolidayDateMap, mergeHolidayDateMaps } from "common/calendar";

/**
 * Preselects the admin's own region
 */
export const usePreselectLeaveRegion = () => {
	const profile = useSelector((state) => state.profile);
	const [searchParams, setSearchParams] = useSearchParams();
	const initialised = useRef(false);

	useEffect(() => {
		if (initialised.current || !profile || !Object.keys(profile).length) return;

		initialised.current = true;

		if (searchParams.getAll("region").length) return;

		setSearchParams({ region: [profile.holidayRegionCode || DEFAULT_HOLIDAY_REGION] }, { replace: true });
	}, [profile, searchParams, setSearchParams]);
};

/**
 * Region filter shared by the leave calendars. An empty selection means all regions */
const useLeaveRegionFilter = (year) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [regionOptions, setRegionOptions] = useState([]);
	const [holiday, setHoliday] = useState({});

	const regions = useMemo(() => searchParams.getAll("region"), [searchParams]);

	const holidayRegionsKey = useMemo(() => (regions.length ? regions : regionOptions.map((o) => o.value)).join(","), [regions, regionOptions]);

	//prettier-ignore
	const filterLeaves = useCallback((leaves) => {
		return regions.length ? leaves.filter((o) => regions.includes(o.userRegion)) : leaves;
	}, [regions]);

	//prettier-ignore
	const onChangeRegion = useCallback((values) => {
		setSearchParams(values.length ? { region: values } : {}, { replace: true });
	}, [setSearchParams]);

	useEffect(() => {
		let ignore = false;

		getHolidayRegionListing()
			.then((res) => {
				if (!ignore) setRegionOptions(res.content);
			})
			.catch(() => {});

		return () => {
			ignore = true;
		};
	}, []);

	useEffect(() => {
		if (!holidayRegionsKey) return;

		let ignore = false;

		Promise.all(holidayRegionsKey.split(",").map((o) => api.get.holidayCalendar.yearByRegion(year, o)))
			.then((responses) => {
				if (!ignore) setHoliday(mergeHolidayDateMaps(responses.map(getHolidayDateMap)));
			})
			.catch((error) => {
				if (!ignore) serveRequestErrors(error);
			});

		return () => {
			ignore = true;
		};
	}, [year, holidayRegionsKey]);

	return { regions, regionOptions, holiday, filterLeaves, onChangeRegion };
};

export default useLeaveRegionFilter;
